import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Mail,
  Calendar,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sort icon component - moved outside to prevent re-renders
const SortIcon = ({ column, sortBy, sortDirection }) => {
  if (sortBy !== column) {
    return <ArrowUpDown className="w-4 h-4 ml-1 text-slate-400" />;
  }
  return sortDirection === "asc" 
    ? <ArrowUp className="w-4 h-4 ml-1 text-purple-600" />
    : <ArrowDown className="w-4 h-4 ml-1 text-purple-600" />;
};

const Referrals = () => {
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [commissionRate, setCommissionRate] = useState(30);
  
  // Filter and sort state
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("signupDate");
  const [sortDirection, setSortDirection] = useState("desc");

  const fetchReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        page: pagination.page.toString(),
        pageSize: pagination.pageSize.toString(),
        sortBy: sortBy,
        sortDirection: sortDirection
      });

      const response = await axios.get(`${API}/referrals?${params.toString()}`, {
        withCredentials: true
      });

      setReferrals(response.data.referrals);
      setPagination(response.data.pagination);
      setCommissionRate(response.data.partnerCommissionRate);
    } catch (error) {
      console.error("Referrals fetch error:", error);
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, pagination.page, pagination.pageSize, sortBy, sortDirection]);

  useEffect(() => {
    document.title = "Referrals | GeniusList Partners";
    fetchReferrals();
  }, [fetchReferrals]);

  // Reset to page 1 when filter changes
  const handleStatusChange = (newStatus) => {
    setStatusFilter(newStatus);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("desc");
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Pagination handlers
  const goToNextPage = () => {
    if (pagination.hasNext) {
      setPagination(prev => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const goToPrevPage = () => {
    if (pagination.hasPrev) {
      setPagination(prev => ({ ...prev, page: prev.page - 1 }));
    }
  };

  // Status badge styling
  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-emerald-50 text-emerald-700 border border-emerald-200",
      pending: "bg-amber-50 text-amber-700 border border-amber-200",
      churned: "bg-rose-50 text-rose-700 border border-rose-200"
    };
    return styles[status] || styles.pending;
  };

  // Plan badge styling
  const getPlanBadge = (plan) => {
    const styles = {
      basic: "bg-slate-100 text-slate-700",
      premium: "bg-purple-100 text-purple-700",
      brokerage: "bg-blue-100 text-blue-700"
    };
    return styles[plan] || styles.basic;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      year: "numeric" 
    });
  };

  // Status filter buttons config
  const statusFilters = [
    { value: "all", label: "All", count: null },
    { value: "active", label: "Active", count: null },
    { value: "pending", label: "Pending", count: null },
    { value: "churned", label: "Churned", count: null }
  ];

  if (loading && referrals.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="bg-white border-slate-100">
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-6">
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="h-10 w-24" />
              ))}
            </div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-purple-50 text-purple-600">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Referrals</p>
              <p className="text-2xl font-bold text-slate-900">{pagination.totalCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Commission Rate</p>
              <p className="text-2xl font-bold text-slate-900">{commissionRate}%</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Showing</p>
              <p className="text-2xl font-bold text-slate-900">
                {referrals.length} of {pagination.totalCount}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card className="bg-white border-slate-100 shadow-sm">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              All Referrals
            </CardTitle>
            
            {/* Status Filter Pills */}
            <div className="flex gap-2 flex-wrap">
              {statusFilters.map((filter) => (
                <Button
                  key={filter.value}
                  variant={statusFilter === filter.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleStatusChange(filter.value)}
                  className={statusFilter === filter.value 
                    ? "gradient-btn text-white" 
                    : "text-slate-600"
                  }
                  data-testid={`filter-${filter.value}`}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Customer</TableHead>
                  <TableHead className="min-w-[100px]">Plan</TableHead>
                  <TableHead 
                    className="min-w-[100px] cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center">
                      Status
                      <SortIcon column="status" sortBy={sortBy} sortDirection={sortDirection} />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="min-w-[120px] cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort("signupDate")}
                  >
                    <div className="flex items-center">
                      Signup Date
                      <SortIcon column="signupDate" sortBy={sortBy} sortDirection={sortDirection} />
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[130px] text-right">Monthly Commission</TableHead>
                  <TableHead 
                    className="min-w-[120px] text-right cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort("lifetimeEarnings")}
                  >
                    <div className="flex items-center justify-end">
                      Lifetime Earned
                      <SortIcon column="lifetimeEarnings" sortBy={sortBy} sortDirection={sortDirection} />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 py-12">
                      <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p>No referrals found</p>
                      {statusFilter !== "all" && (
                        <Button
                          variant="link"
                          onClick={() => handleStatusChange("all")}
                          className="text-purple-600 mt-2"
                        >
                          Clear filters
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  referrals.map((referral, idx) => (
                    <TableRow 
                      key={referral.referral_id || idx} 
                      className="hover:bg-slate-50"
                      data-testid={`referral-row-${idx}`}
                    >
                      {/* Customer Name & Email */}
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900">
                            {referral.referred_name}
                          </span>
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {referral.email}
                          </span>
                        </div>
                      </TableCell>
                      
                      {/* Plan */}
                      <TableCell>
                        <Badge className={`${getPlanBadge(referral.plan)} capitalize`}>
                          {referral.plan || "basic"}
                        </Badge>
                      </TableCell>
                      
                      {/* Status */}
                      <TableCell>
                        <Badge className={`${getStatusBadge(referral.status)} capitalize`}>
                          {referral.status}
                        </Badge>
                      </TableCell>
                      
                      {/* Signup Date */}
                      <TableCell className="text-slate-600">
                        {formatDate(referral.signup_date)}
                      </TableCell>
                      
                      {/* Monthly Commission */}
                      <TableCell className="text-right font-medium text-emerald-600">
                        ${(referral.monthly_commission ?? (referral.plan_price * (commissionRate / 100))).toFixed(2)}
                      </TableCell>
                      
                      {/* Lifetime Earned */}
                      <TableCell className="text-right font-bold text-slate-900">
                        ${(referral.lifetime_earned ?? referral.commission_earned ?? 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                Page {pagination.page} of {pagination.totalPages}
                <span className="hidden sm:inline"> • {pagination.totalCount} total referrals</span>
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPrevPage}
                  disabled={!pagination.hasPrev}
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextPage}
                  disabled={!pagination.hasNext}
                  data-testid="pagination-next"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Referrals;
